<script setup lang="ts">
  import { computed, unref } from 'vue';
  // Components
  import {
    DatePicker,
    IconImage,
    FilterModal,
    NumberStepperInput,
    ThemeProvider,
    SelectInput,
  } from '@/shared/ui';
  // Assets
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  // Composables
  import { useDocumentFilterModal } from '@/features/Document';
  import { useDocumentFilterCollaborators } from '../../model';
  import { useFilterStore } from '@/entities/Filter';
  import type { FilterOptions } from '@/entities/Document';
  import type { CaslAction, Subjects } from '@/shared/lib/casl';

  type PermissionCheck = {
    action: CaslAction;
    subject: Subjects;
  };

  const props = withDefaults(
    defineProps<{
      currentFilters?: FilterOptions;
      variant?: 'default' | 'approval' | 'verification';
      elaborationPermission?: PermissionCheck;
    }>(),
    {
      variant: 'default',
      elaborationPermission: () => ({ action: 'send', subject: 'Document' }),
    }
  );

  /** As duas telas de homologação compartilham o enxugamento do modal; só a Verificação acrescenta. */
  const isHomologationVariant = computed(
    () => props.variant === 'approval' || props.variant === 'verification'
  );

  const isVerificationVariant = computed(() => props.variant === 'verification');

  /** 392px é a largura das duas telas de homologação no design; o padrão do FilterModal é 672px. */
  const HOMOLOGATION_CONTENT_CLASS = 'w-[392px] max-w-[92vw]';

  /** Recuo de 24px, 16px acima do rótulo e 24px abaixo do campo, medidos nos nós do design. */
  const HOMOLOGATION_SECTION_CLASS = 'px-6 pt-4 pb-6';

  const filterModalExtraProps = computed(() =>
    isHomologationVariant.value
      ? {
          buttonsText: { clear: 'Limpar filtros', apply: 'Aplicar filtros' },
          contentClass: HOMOLOGATION_CONTENT_CLASS,
        }
      : {}
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: FilterOptions): void;
    (e: 'update:selectedDate', date: Date | null): void;
    (e: 'clear-filters'): void;
  }>();

  const { fetchCollaborators, elaborationUsersList, approvalMembersList } =
    useDocumentFilterCollaborators(props.elaborationPermission, isVerificationVariant.value);

  const filterStore = useFilterStore();

  const {
    formData,
    requestDate,
    isRequestDateOpen,
    formatDate,
    clearFilters,
    applyFilters,
    loadFilters,
  } = useDocumentFilterModal(
    emit,
    fetchCollaborators,
    (props.currentFilters || filterStore.filters) as Partial<FilterOptions>
  );

  /**
   * O "X" cancela: fecha reaplicando os filtros que já estavam valendo, para o que o usuário
   * digitou sem aplicar não vazar para a lista.
   */
  const handleClose = () => {
    const applied = props.currentFilters ?? filterStore.filters;
    if (applied && typeof applied === 'object' && Object.keys(applied).length > 0) {
      loadFilters(applied as Partial<FilterOptions>);
    }
    emit('close');
  };

  const safeFormData = computed(() => {
    const currentFormData = unref(formData);

    return {
      ...currentFormData,
      elaborationApproval: currentFormData.elaborationApproval ?? '',
      elaboratedBy: currentFormData.elaboratedBy ?? '',
      approvedBy: currentFormData.approvedBy ?? '',
    };
  });

  /**
   * O valor selecionado é sempre um id de `User`; o que muda é a chave de destino. A Verificação
   * escreve em `elaboratedBy`, que é o que as duas filas declaram; as outras variantes seguem
   * escrevendo em `elaborationApproval`, que o mapeador manda em `user`.
   */
  const elaborationFieldValue = computed(() =>
    isVerificationVariant.value
      ? safeFormData.value.elaboratedBy
      : safeFormData.value.elaborationApproval
  );

  const handleElaborationSelected = (value: string | number) => {
    const target = unref(formData);
    if (isVerificationVariant.value) {
      target.elaboratedBy = String(value);
      return;
    }
    target.elaborationApproval = value as string;
  };

  // `userOptions.id` é `number` e `Filters.approvedBy` é `string`: o `SelectInput` emite `any`, e
  // sem esta conversão o compilador não reclamaria de um número indo numa chave de texto.
  const handleApprovalSelected = (value: string | number) => {
    unref(formData).approvedBy = String(value);
  };

  const handleClearFilters = () => {
    clearFilters();
    emit('clear-filters');
  };
</script>

<template>
  <FilterModal
    v-bind="filterModalExtraProps"
    @close="handleClose"
    @clear-filters="handleClearFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <ThemeProvider>
        <div :class="isHomologationVariant ? '' : 'px-6 space-y-4'">
          <div
            :class="[
              'flex gap-4 border-b border-gray-02',
              isHomologationVariant ? HOMOLOGATION_SECTION_CLASS : 'px-4 py-12 pb-6',
            ]"
          >
            <div :class="isHomologationVariant ? 'w-full' : 'w-1/2'">
              <h4 :class="['font-medium text-gray-07', isHomologationVariant ? 'mb-2' : 'pb-4']">
                {{ isVerificationVariant ? 'Data da Solicitação' : 'Data' }}
              </h4>
              <button
                :class="[
                  'flex items-center bg-white-01 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium gap-2',
                  isHomologationVariant ? 'w-[182px] px-3 text-sm' : 'px-4',
                ]"
                @click="isRequestDateOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-5 w-5"
                />
                <span :class="isHomologationVariant ? 'whitespace-nowrap' : 'grow text-center'">{{
                  requestDate !== null ? formatDate(requestDate) : 'Selecione uma data'
                }}</span>
              </button>
              <DatePicker
                v-model:selected-date="requestDate"
                v-model:is-open="isRequestDateOpen"
                class="my-auto"
                @apply="isRequestDateOpen = false"
                @cancel="isRequestDateOpen = false"
              />
            </div>
          </div>

          <div>
            <div
              :class="[
                'flex gap-4 border-b border-gray-02',
                isHomologationVariant ? HOMOLOGATION_SECTION_CLASS : 'px-4 pb-6',
              ]"
            >
              <div :class="isHomologationVariant ? 'w-full' : 'w-1/2'">
                <h4 class="font-medium text-gray-07 mb-2">
                  Elaboração
                </h4>
                <SelectInput
                  data-testid="filter-elaboration-select"
                  :model-value="elaborationFieldValue"
                  :options="elaborationUsersList"
                  label="Selecione um colaborador"
                  max-height="12rem"
                  @update:model-value="handleElaborationSelected"
                />
              </div>
            </div>

            <div
              v-if="isVerificationVariant"
              :class="['flex gap-4 border-b border-gray-02', HOMOLOGATION_SECTION_CLASS]"
            >
              <div class="w-full">
                <h4 class="font-medium text-gray-07 mb-2">
                  Aprovação
                </h4>
                <SelectInput
                  data-testid="filter-approval-select"
                  :model-value="safeFormData.approvedBy"
                  :options="approvalMembersList"
                  label="Selecione um colaborador"
                  max-height="12rem"
                  @update:model-value="handleApprovalSelected"
                />
              </div>
            </div>

            <div
              v-if="isVerificationVariant"
              class="flex gap-4 px-6 pt-6 pb-6"
            >
              <div class="w-[100px]">
                <h4 class="font-medium text-gray-07 mb-2">
                  Revisão
                </h4>
                <NumberStepperInput
                  data-testid="filter-revision-stepper"
                  :model-value="formData.revision ?? 0"
                  :min="0"
                  :step="1"
                  input-class="w-full pr-9"
                  wrapper-class="w-full"
                  @update:model-value="formData.revision = $event ?? 0"
                />
              </div>
            </div>

            <div
              v-if="!isHomologationVariant"
              class="flex p-6"
            >
              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 pb-4">
                  Tipo
                </h4>
                <div class="flex flex-col items-start gap-2">
                  <button
                    :class="[
                      'py-2 px-3 rounded-lg  text-left',
                      formData.type === 'DOCUMENT'
                        ? 'bg-(--theme-secondary) text-white'
                        : 'text-gray-07',
                    ]"
                    @click="formData.type = 'DOCUMENT'"
                  >
                    Documento
                  </button>
                  <button
                    :class="[
                      'py-2 px-3 rounded-lg  text-left',
                      formData.type === 'REGISTRATION'
                        ? 'bg-(--theme-secondary) text-white'
                        : 'text-gray-07',
                    ]"
                    @click="formData.type = 'REGISTRATION'"
                  >
                    Registro
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
